import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Phone, Loader2, Inbox } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const TYPE_FILTERS = ["all", "distributor", "bulk", "product", "contact"];
const STATUSES = ["new", "contacted", "qualified", "closed"];
const STATUS_STYLES = {
  new: "bg-brand-gold/15 text-brand-deep border-brand-gold/40",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  qualified: "bg-brand-deep/10 text-brand-deep border-brand-deep/30",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
};

function exportCsv(leads) {
  const headers = ["Date", "Type", "Status", "Name", "Business", "Phone", "WhatsApp", "District", "City", "Business Type", "Products", "Requirement", "Product", "Quantity", "Preferred Contact", "Message", "Notes"];
  const rows = leads.map((l) => [
    new Date(l.created_at).toLocaleString(), l.type, l.status, l.name, l.business_name, l.phone, l.whatsapp,
    l.district, l.city, l.business_type, (l.products_interested || []).join("; "), l.estimated_requirement,
    l.product, l.quantity, l.preferred_contact, (l.message || "").replace(/\n/g, " "), (l.notes || "").replace(/\n/g, " "),
  ]);
  const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `icropsia-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function LeadsManager() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/leads${filter !== "all" ? `?type=${filter}` : ""}`);
      setLeads(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const updateLead = async (id, patch) => {
    setSavingId(id);
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, patch);
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      toast.success("Lead updated.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const removeLead = async (id) => {
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="leads-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex gap-2 flex-wrap">
          {TYPE_FILTERS.map((t) => (
            <button
              key={t}
              data-testid={`leads-filter-${t}`}
              onClick={() => setFilter(t)}
              className={`rounded-full px-4 py-2 text-xs font-bold capitalize transition-all ${
                filter === t ? "bg-brand-deep text-white" : "bg-white text-brand-deep/60 border border-brand-deep/15"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <button
          onClick={() => exportCsv(leads)}
          data-testid="leads-export-csv"
          className="flex items-center gap-2 rounded-full bg-brand-gold px-4 py-2 text-xs font-bold text-brand-deep hover:bg-brand-wheat transition-all"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-brand-deep animate-spin" /></div>
      ) : leads.length === 0 ? (
        <div className="py-16 text-center text-brand-charcoal/40">
          <Inbox className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No enquiries yet. New form submissions will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {leads.map((lead) => (
            <div key={lead.id} data-testid={`lead-card-${lead.id.slice(0, 8)}`} className="bg-white rounded-2xl border border-brand-deep/10 p-5 sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h3 className="font-display font-bold text-brand-deep text-lg">{lead.name}</h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-brand-deep text-white px-2.5 py-1 rounded-full">{lead.type}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider border px-2.5 py-1 rounded-full ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`}>
                      {lead.status}
                    </span>
                  </div>
                  <p className="text-xs text-brand-charcoal/45 mt-1">{new Date(lead.created_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  {lead.phone && (
                    <a href={`tel:+91${String(lead.phone).replace(/\D/g, "").slice(-10)}`} data-testid="lead-call-button" className="w-9 h-9 rounded-full bg-brand-deep/5 border border-brand-deep/15 flex items-center justify-center text-brand-deep hover:bg-brand-deep hover:text-white transition-all">
                      <Phone className="w-4 h-4" />
                    </a>
                  )}
                  <button onClick={() => removeLead(lead.id)} data-testid="lead-delete-button" className="w-9 h-9 rounded-full bg-red-50 border border-red-200 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-all">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                {[["Business", lead.business_name], ["Phone", lead.phone], ["WhatsApp", lead.whatsapp], ["District", lead.district], ["City", lead.city], ["Type", lead.business_type], ["Requirement", lead.estimated_requirement], ["Product", lead.product], ["Quantity", lead.quantity], ["Contact via", lead.preferred_contact]]
                  .filter(([, v]) => v)
                  .map(([label, v]) => (
                    <div key={label}>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-brand-deep/40 block">{label}</span>
                      <span className="text-brand-charcoal/80">{v}</span>
                    </div>
                  ))}
                {(lead.products_interested || []).length > 0 && (
                  <div className="col-span-2 sm:col-span-3 lg:col-span-4">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-brand-deep/40 block">Products Interested</span>
                    <span className="text-brand-charcoal/80">{lead.products_interested.join(", ")}</span>
                  </div>
                )}
                {lead.message && (
                  <div className="col-span-2 sm:col-span-3 lg:col-span-4">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-brand-deep/40 block">Message</span>
                    <span className="text-brand-charcoal/80">{lead.message}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-brand-deep/5 flex flex-col sm:flex-row gap-3">
                <select
                  value={lead.status}
                  onChange={(e) => updateLead(lead.id, { status: e.target.value })}
                  data-testid="lead-status-select"
                  className="input-field sm:w-44 !py-2 text-xs font-bold capitalize"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <div className="flex flex-1 gap-2">
                  <input
                    defaultValue={lead.notes || ""}
                    data-testid="lead-notes-input"
                    placeholder="Internal notes…"
                    className="input-field !py-2 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") updateLead(lead.id, { notes: e.target.value });
                    }}
                    onBlur={(e) => {
                      if (e.target.value !== (lead.notes || "")) updateLead(lead.id, { notes: e.target.value });
                    }}
                  />
                  {savingId === lead.id && <Loader2 className="w-4 h-4 text-brand-deep animate-spin self-center" />}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
